import { useRef, useState } from "react";
import {
  Box,
  HStack,
  IconButton,
  Input,
  Stack,
  Text,
  Tooltip,
} from "@chakra-ui/react";
import { CloseIcon } from "@chakra-ui/icons";
import { ChevronDown, ChevronUp, GripVertical } from "lucide-react";
import type { ProfileLink } from "../lib/profileLinks";

/** Loose check only: this MARKS a row, it never blocks the save. Support staff
 *  sometimes need to store something this wouldn't approve of, and the backend
 *  is the real authority. */
function looksLikeUrl(url: string): boolean {
  const trimmed = url.trim();
  if (!trimmed) return true; // an empty new row is not "invalid" yet
  try {
    const parsed = new URL(trimmed);
    return parsed.protocol === "http:" || parsed.protocol === "https:";
  } catch {
    return false;
  }
}

/**
 * Editor for a creator's profile links: one row per link, `[Name] [URL]`.
 *
 * Replaces `StringListEditor` for `links` / `social_links`, which rendered the
 * stored value verbatim — so a support user editing a customer-app link saw the
 * literal text `{"url":"https://…","name":"…"}` in a text box.
 *
 * NO cap on the number of links: support acts as an override tool, so the count
 * is shown but never blocks. The 4-link cap remains creator-facing in
 * customer-app, which must therefore render and preserve every link it receives
 * and only prevent ADDING past the cap.
 */
export default function LinkListEditor({
  values,
  onChange,
  namePlaceholder = "Name",
  showName = true,
}: {
  values: ProfileLink[];
  onChange: (next: ProfileLink[]) => void;
  namePlaceholder?: string;
  /** socials carry no name today, so their editor is URL-only. */
  showName?: boolean;
}) {
  const dragFrom = useRef<number | null>(null);
  const [dragOver, setDragOver] = useState<number | null>(null);

  const update = (i: number, patch: Partial<ProfileLink>) => {
    const next = values.map((link, idx) => (idx === i ? { ...link, ...patch } : link));
    // A blank name is omitted rather than stored as "", matching parseLinks --
    // otherwise {url} and {url, name: ""} round-trip as different values and
    // diff() sees a change that isn't one.
    onChange(
      next.map((link) =>
        link.name && link.name.trim() ? link : { url: link.url },
      ),
    );
  };

  const move = (from: number, to: number) => {
    if (to < 0 || to >= values.length || from === to) return;
    const next = [...values];
    const [moved] = next.splice(from, 1);
    next.splice(to, 0, moved);
    onChange(next);
  };

  return (
    <Stack spacing={1.5}>
      {values.map((link, i) => {
        const invalid = !looksLikeUrl(link.url);
        return (
          <HStack
            key={i}
            spacing={1}
            align="center"
            draggable
            onDragStart={() => (dragFrom.current = i)}
            onDragOver={(e) => {
              e.preventDefault();
              setDragOver(i);
            }}
            onDragLeave={() => setDragOver((d) => (d === i ? null : d))}
            onDrop={(e) => {
              e.preventDefault();
              if (dragFrom.current !== null) move(dragFrom.current, i);
              dragFrom.current = null;
              setDragOver(null);
            }}
            onDragEnd={() => {
              dragFrom.current = null;
              setDragOver(null);
            }}
            borderTopWidth={dragOver === i ? "2px" : "0"}
            borderTopColor="blue.400"
          >
            <Box color="gray.400" cursor="grab" aria-hidden flexShrink={0}>
              <GripVertical size={14} />
            </Box>
            {/* Drag is mouse-only, so ordering also gets a keyboard path. */}
            <Stack spacing={0} flexShrink={0}>
              <IconButton
                size="xs"
                variant="ghost"
                minW={5}
                h={4}
                aria-label={`Move link ${i + 1} up`}
                isDisabled={i === 0}
                icon={<ChevronUp size={12} />}
                onClick={() => move(i, i - 1)}
              />
              <IconButton
                size="xs"
                variant="ghost"
                minW={5}
                h={4}
                aria-label={`Move link ${i + 1} down`}
                isDisabled={i === values.length - 1}
                icon={<ChevronDown size={12} />}
                onClick={() => move(i, i + 1)}
              />
            </Stack>
            {showName && (
              <Input
                size="sm"
                flex="1"
                value={link.name ?? ""}
                placeholder={namePlaceholder}
                aria-label={`Link ${i + 1} name`}
                onChange={(e) => update(i, { name: e.target.value })}
              />
            )}
            <Tooltip
              label="Doesn't look like an http(s) URL — you can still save it"
              isDisabled={!invalid}
              openDelay={300}
            >
              <Input
                size="sm"
                flex="2"
                isInvalid={invalid}
                value={link.url}
                placeholder="https://…"
                aria-label={`Link ${i + 1} URL`}
                onChange={(e) => update(i, { url: e.target.value })}
              />
            </Tooltip>
            <IconButton
              size="xs"
              variant="ghost"
              aria-label={`Remove link ${i + 1}`}
              icon={<CloseIcon boxSize={2} />}
              onClick={() => onChange(values.filter((_, idx) => idx !== i))}
            />
          </HStack>
        );
      })}
      <HStack spacing={3}>
        <Box
          as="button"
          type="button"
          fontSize="xs"
          color="blue.600"
          onClick={() => onChange([...values, { url: "" }])}
        >
          + Add link
        </Box>
        {values.length > 0 && (
          <Text fontSize="xs" color="gray.500">
            {values.length} {values.length === 1 ? "link" : "links"}
          </Text>
        )}
      </HStack>
    </Stack>
  );
}
